// The SWAP TRANSITION — "kill the blink" (ISLAND PR2, the arch-eval's must-nail
// #2). An htmx fragment swap replaces innerHTML/outerHTML and the browser
// re-paints the new structure INSTANTLY: a reordered feed JUMPS to its new
// order, an opened challenge POPS in, a departed pane VANISHES. That instant
// repaint is the BLINK. Canon 75 §1 rule 2 ("items move, they do not blink" on
// rail reorder) and canon 77 §3 (ink settles; a tie's proposed→accepted weight
// is a settle, not a jump) demand the swap be a MOTION: items FLIP to their new
// places, a fragment that appears ENTERS, one that leaves EXITS, and a tie whose
// weight changed SETTLES into it.
//
// This module is the DOM half. All of its motion goes THROUGH motion.ts — the
// island's one motion language (canon: "one consistent motion language, not two
// systems fighting"), the same four springs the overlay drag/resize/stack and
// the electric register speak. It adds NO new dependency: Motion One (already
// adopted in docs/dependencies.md, which names "swap/FLIP transitions" as a
// follow-up consumer) does the springs; the native CSS View Transitions path was
// considered and declined below. The load-bearing arithmetic — what moved, what
// entered, what left, and the inverted transform — is the PURE flip.ts, unit-
// tested; this module only measures the DOM, matches keys, and plays the plan.
//
// prefers-reduced-motion is honoured through motion.ts, once, for the whole
// island (canon 94 §7 / canon 83 — NON-NEGOTIABLE): under reduced motion every
// helper here jumps to the end state with no FLIP, no enter travel, no exit
// ghost — the new structure simply appears, which is the correct no-motion
// behaviour (the content is never withheld, only the movement). This module adds
// nothing that can forget that, because it animates only through motion.ts.
//
// Lights, not cells (canon 83/94): the transition MOVES and FADES; it draws no
// border, card, fence or cursor. Polish is motion quality, not chrome.
//
// WHY Motion One FLIP and not CSS View Transitions (htmx `transition:true`).
// View Transitions are genuinely zero-JS and were tried first, as the brief
// asks. Two reasons the island drives the FLIP itself instead:
//   1. ONE motion language. The field already settles everything (drag release,
//      raise, resize, reflow, the electric charge) on motion.ts's named springs.
//      A View-Transition swap would introduce a SECOND motion system with its own
//      UA-default easing/curve that cannot be tuned to the field's SETTLE spring —
//      exactly the "two systems fighting" the motion.ts adoption retired. FLIP
//      through motion.ts makes a reorder settle with the SAME hand as a pane
//      release.
//   2. Reduced-motion + reach. The View-Transitions API is still uneven across
//      engines and its reduced-motion story is the UA's, not ours; motion.ts owns
//      prefers-reduced-motion for the whole island in one place, so the swap's
//      no-motion path is identical to every other island surface's. (If a future
//      engine makes `view-transition-name` FLIP with a tunable spring, it drops
//      in behind this same seam.)

import {
  planFlip,
  invertTransform,
  IDENTITY,
  type Box,
} from "./flip.js";
import {
  prefersReducedMotion,
  springTo,
  easeTo,
  SETTLE,
  CHARGE,
} from "./motion.js";

// How far an entering fragment rises as it fades in, and an exiting one falls as
// it fades out — a small vertical travel so the enter/exit reads as the field
// lifting a thing in / letting it go, not a bare opacity flicker. px.
const ENTER_RISE = 10;
const EXIT_FALL = 8;
const ENTER_MS = 240;
const EXIT_MS = 180;

/**
 * Resolve a stable FLIP key for an element, or null if it has none. The key is
 * what matches an item across the swap (same key before and after = it MOVED;
 * only-after = ENTERED; only-before = EXITED). In priority:
 *   1. an explicit `data-flip-key` (a surface can name its own identity),
 *   2. `data-take` (a take li), `data-object`+`data-word` (a keyword),
 *   3. the element id (a challenge view `challenge-{uuid}`, a split pane),
 *   4. the href of a contained anchor (the feed's `<li><a href="/challenge/{id}">`
 *      — the li itself carries no id, so its identity is the challenge it links).
 * Anything with no stable identity returns null and is simply not FLIPped (it
 * will still enter/exit by its container's motion if it is the swap root).
 */
export function flipKey(el: Element): string | null {
  const he = el as HTMLElement;
  const explicit = he.dataset?.flipKey;
  if (explicit) return `k:${explicit}`;
  const take = he.dataset?.take;
  if (take) return `take:${take}`;
  if (he.dataset?.object === "keyword" && he.dataset?.word) {
    return `kw:${he.dataset.word}:${he.dataset.take ?? ""}`;
  }
  if (el.id) return `id:${el.id}`;
  const a = el.querySelector<HTMLAnchorElement>(":scope > a[href], a[href]");
  if (a?.getAttribute("href")) return `href:${a.getAttribute("href")}`;
  return null;
}

/** The keyed, FLIP-able children of a swap container: its DIRECT element
 *  children that resolve to a key. We FLIP one level (the reordered list items /
 *  the panes), not the whole subtree — the children are what move; their
 *  contents ride along inside them. */
function keyedChildren(container: Element): Map<string, HTMLElement> {
  const out = new Map<string, HTMLElement>();
  for (const child of Array.from(container.children)) {
    const key = flipKey(child);
    if (key && !out.has(key)) out.set(key, child as HTMLElement);
  }
  return out;
}

function boxOf(el: Element): Box {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
}

/** A tie's weight, read from its class (`tie-proposed` / `tie-accepted` /
 *  `tie-faint`) — the one state a tie shows (canon 77 §3). Used to detect a
 *  proposed→accepted settle across a swap. */
function tieWeight(el: Element): string | null {
  for (const c of Array.from(el.classList)) {
    if (c.startsWith("tie-")) return c.slice(4);
  }
  return null;
}

/** Keyed ties under a container, by the pair of take ids they join (the stable
 *  identity of a tie), → their weight. A tie present before and after whose
 *  weight CHANGED is a settle. */
function tieWeights(container: Element): Map<string, string> {
  const out = new Map<string, string>();
  for (const tie of Array.from(container.querySelectorAll(".tie[data-a][data-b], .member-line[data-a][data-b]"))) {
    const a = (tie as HTMLElement).dataset.a ?? "";
    const b = (tie as HTMLElement).dataset.b ?? "";
    const w = tieWeight(tie);
    if (a && b && w) out.set(`${a}|${b}`, w);
  }
  return out;
}

/** A captured FIRST state, taken on htmx:beforeSwap: the box of every keyed
 *  child, a detached CLONE of each (so an item that LEAVES can still be shown
 *  exiting after htmx has removed the real node), and the tie weights. */
interface FirstState {
  boxes: Map<string, Box>;
  clones: Map<string, HTMLElement>;
  containerBox: Box;
  ties: Map<string, string>;
}

// FIRST states are keyed TWO ways, because an outerHTML swap REPLACES the target
// element (a new node with the same id), so the old element reference is useless
// on afterSwap. An element with an id is recovered by id; one without falls back
// to the element reference (an innerHTML swap keeps the same node). The WeakMap
// holds the no-id case (GC-safe); the id map holds the outerHTML case and is
// cleared as each play consumes it.
const SWAP_STATE = new WeakMap<Element, FirstState>();
const SWAP_STATE_BY_ID = new Map<string, FirstState>();

/** Capture the FIRST state of a swap target, just before htmx replaces it. We
 *  record the container's own box too, so an exit ghost can be positioned
 *  relative to it. Clones are deep + detached; they carry no htmx wiring and are
 *  used only as fading exit ghosts. Stored by id when the target has one (so an
 *  outerHTML replacement can recover it), else by element reference. */
function captureFirst(container: Element): void {
  const children = keyedChildren(container);
  const boxes = new Map<string, Box>();
  const clones = new Map<string, HTMLElement>();
  for (const [key, el] of children) {
    boxes.set(key, boxOf(el));
    const clone = el.cloneNode(true) as HTMLElement;
    clones.set(key, clone);
  }
  const state: FirstState = {
    boxes,
    clones,
    containerBox: boxOf(container),
    ties: tieWeights(container),
  };
  if (container.id) SWAP_STATE_BY_ID.set(container.id, state);
  else SWAP_STATE.set(container, state);
}

/**
 * Play the transition after htmx has swapped in the new structure. Reads the
 * LAST boxes, asks flip.ts which keys moved / entered / left, then:
 *   - FLIP each mover: write its inverted transform (so it visually starts where
 *     it was), force a reflow, then SPRING the transform to identity — it travels
 *     and resizes into its new place (canon 75 §1 rule 2).
 *   - ENTER each newcomer: from faded + risen to full (canon 77: ink settles in).
 *   - EXIT each departed key: a positioned ghost clone fades + falls, then is
 *     removed (the real node is already gone; the ghost is the only way to show
 *     a leave after an htmx swap).
 *   - SETTLE each tie whose weight changed proposed→accepted (canon 77 §3).
 * Under reduced motion every branch lands the end state instantly via motion.ts.
 */
function playTransition(container: Element): void {
  // Recover the FIRST state: by id first (an outerHTML swap replaced the node,
  // so the live `container` is a NEW element with the same id), else by the
  // element reference (an innerHTML swap kept the same node).
  let first = container.id ? SWAP_STATE_BY_ID.get(container.id) : undefined;
  if (first) SWAP_STATE_BY_ID.delete(container.id);
  else {
    first = SWAP_STATE.get(container);
    SWAP_STATE.delete(container);
  }
  if (!first) return;

  const lastChildren = keyedChildren(container);
  const lastBoxes = new Map<string, Box>();
  for (const [key, el] of lastChildren) lastBoxes.set(key, boxOf(el));

  const plan = planFlip(first.boxes, lastBoxes);

  // ---- FLIP the movers -----------------------------------------------------
  for (const m of plan.moved) {
    const el = lastChildren.get(m.key);
    if (!el) continue;
    if (prefersReducedMotion()) continue; // no-motion: it is already at Last
    // Invert: the element is at Last; place it visually at First.
    const prevOrigin = el.style.transformOrigin;
    const prevTransition = el.style.transition;
    el.style.transformOrigin = "0 0";
    el.style.transition = "none";
    el.style.transform = invertTransform(m.invert);
    el.style.willChange = "transform";
    // Force the inverted frame to commit before we spring it away, so the browser
    // does not coalesce invert+play into no visible travel.
    void (el as HTMLElement).offsetWidth;
    // Play: spring the transform back to identity with the field's SETTLE spring —
    // the SAME settle a released pane uses, so a reorder reads in the one hand.
    springTo(
      el,
      { transform: invertTransform(IDENTITY) },
      SETTLE,
    ).finished.then(() => {
      el.style.transform = "";
      el.style.transformOrigin = prevOrigin;
      el.style.transition = prevTransition;
      el.style.willChange = "";
    });
  }

  // ---- ENTER the newcomers -------------------------------------------------
  for (const key of plan.entered) {
    const el = lastChildren.get(key);
    if (!el) continue;
    enter(el);
  }

  // ---- EXIT the departed (ghost clones) ------------------------------------
  if (!prefersReducedMotion()) {
    for (const { key, box } of plan.exited) {
      const clone = first.clones.get(key);
      if (!clone) continue;
      exitGhost(container, clone, box, first.containerBox);
    }
  }

  // ---- SETTLE a tie whose weight changed -----------------------------------
  const lastTies = tieWeights(container);
  for (const [pair, lastW] of lastTies) {
    const firstW = first.ties.get(pair);
    if (firstW && firstW !== lastW) {
      settleTie(container, pair, lastW);
    }
  }
}

/** Enter one freshly-swapped-in element: from faded + risen to settled. A
 *  spring on opacity/translate so the arrival has the field's lift, not a bare
 *  fade. Reduced motion: motion.ts sets the end state at once. */
export function enter(el: HTMLElement): void {
  if (prefersReducedMotion()) return; // already present, nothing to travel
  el.style.willChange = "transform, opacity";
  // Start state.
  el.style.opacity = "0";
  el.style.transform = `translateY(${ENTER_RISE}px)`;
  void el.offsetWidth;
  easeTo(el, { opacity: 1, transform: "translateY(0px)" }, ENTER_MS).finished.then(() => {
    el.style.opacity = "";
    el.style.transform = "";
    el.style.willChange = "";
  });
}

/** Exit a departed element as a fading, falling GHOST. htmx has already removed
 *  the real node, so we re-attach a detached clone, absolutely positioned at the
 *  element's last box (relative to the container, which we make a positioning
 *  context for the duration), fade+fall it, then remove it. The ghost is
 *  pointer-inert and aria-hidden so it never catches a click or a screen reader
 *  mid-exit. */
function exitGhost(container: Element, clone: HTMLElement, box: Box, containerBox: Box): void {
  const host = container as HTMLElement;
  const prevPos = host.style.position;
  const computed = typeof getComputedStyle === "function" ? getComputedStyle(host).position : "static";
  if (computed === "static") host.style.position = "relative";

  clone.setAttribute("aria-hidden", "true");
  clone.style.position = "absolute";
  clone.style.pointerEvents = "none";
  clone.style.margin = "0";
  clone.style.left = `${box.x - containerBox.x}px`;
  clone.style.top = `${box.y - containerBox.y}px`;
  clone.style.width = `${box.width}px`;
  clone.style.height = `${box.height}px`;
  clone.style.willChange = "transform, opacity";
  clone.style.zIndex = "0";
  host.appendChild(clone);
  void clone.offsetWidth;

  easeTo(clone, { opacity: 0, transform: `translateY(${EXIT_FALL}px)` }, EXIT_MS).finished.then(() => {
    clone.remove();
    // Restore the container's position only if we set it and no other ghost is
    // still mid-exit.
    if (computed === "static" && !host.querySelector(":scope > [aria-hidden='true'][style*='position: absolute']")) {
      host.style.position = prevPos;
    }
  });
}

/** Settle a tie whose weight changed (proposed→accepted, canon 77 §3): the line
 *  does not jump to its new thickness — it CHARGES into it. We brighten and let
 *  the CSS weight land, pulsing the element's own glow with the CHARGE spring
 *  (soft, slow — something becoming firm), so the settle reads as the ink taking.
 *  Reduced motion: the weight class is already applied by the swap; nothing
 *  animates. The tie's resting weight (the CSS class) remains the source of
 *  truth — this only animates the TRANSITION into it. */
function settleTie(container: Element, pair: string, _lastWeight: string): void {
  if (prefersReducedMotion()) return;
  const [a, b] = pair.split("|");
  const el = container.querySelector<HTMLElement>(
    `.tie[data-a="${cssEsc(a)}"][data-b="${cssEsc(b)}"], .member-line[data-a="${cssEsc(a)}"][data-b="${cssEsc(b)}"]`,
  );
  if (!el) return;
  el.style.willChange = "opacity, filter";
  // A brief charge: lift the opacity/weight-glow and let it settle. The tie's
  // own CSS owns the resting look; this is the arrival into it.
  el.style.opacity = "0.4";
  void el.offsetWidth;
  springTo(el, { opacity: 1 }, CHARGE).finished.then(() => {
    el.style.opacity = "";
    el.style.willChange = "";
  });
}

/** Minimal CSS attribute-selector escaping for the take-id values (uuids +
 *  demo ids) we build selectors from — guards a stray quote/backslash. */
function cssEsc(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(s);
  return s.replace(/["\\]/g, "\\$&");
}

/** A handle to detach the swap-transition wiring. */
export interface SwapHandle {
  detach(): void;
  /** Directly drive the transition for a non-htmx environment (the demo harness
   *  swaps the DOM itself, then calls these). Exposed so the harness proves the
   *  REAL motion code, not a reimplementation. */
  capture(container: Element): void;
  play(container: Element): void;
}

/**
 * Wire the swap transition onto a root (the field). It listens for htmx's swap
 * lifecycle:
 *   - `htmx:beforeSwap` — capture the FIRST state of the swap target (the element
 *     htmx is about to replace), so movers/leavers can be measured from it.
 *   - `htmx:afterSwap`  — play the FLIP / enter / exit / tie-settle.
 * Both bubble to the root, so a swap of any descendant is handled. The listeners
 * are harmless where htmx is absent (the demo harness calls capture()/play()
 * directly). Idempotent per element via the SWAP_STATE weak map.
 */
export function attachSwapTransitions(root: HTMLElement): SwapHandle {
  const onBefore = (e: Event) => {
    const target = swapTarget(e);
    if (target) captureFirst(target);
  };
  const onAfter = (e: Event) => {
    let target = swapTarget(e);
    if (!target) return;
    // An outerHTML swap REPLACED the target with a fresh node of the same id; the
    // event's target may be the detached old node or a swapped-in child. If we
    // captured a FIRST state by id and that id is live in the DOM, re-resolve to
    // the LIVE element so Last boxes are measured on the real, attached node.
    if (target.id && SWAP_STATE_BY_ID.has(target.id)) {
      const live = root.ownerDocument?.getElementById(target.id) ?? root.querySelector(`#${cssEsc(target.id)}`);
      if (live) target = live;
    }
    playTransition(target);
  };
  root.addEventListener("htmx:beforeSwap", onBefore as EventListener);
  root.addEventListener("htmx:afterSwap", onAfter as EventListener);

  return {
    detach() {
      root.removeEventListener("htmx:beforeSwap", onBefore as EventListener);
      root.removeEventListener("htmx:afterSwap", onAfter as EventListener);
    },
    capture: captureFirst,
    play: playTransition,
  };
}

/** The element htmx is swapping, from an htmx swap event. htmx puts the target
 *  on `event.detail.target` (beforeSwap/afterSwap); we fall back to the event
 *  target. */
function swapTarget(e: Event): Element | null {
  const detail = (e as CustomEvent).detail as { target?: Element; elt?: Element } | undefined;
  const t = detail?.target ?? (e.target as Element | null);
  return t instanceof Element ? t : null;
}
